import type { Seksjon } from '@origo/seksjon';

/**
 * Returnerer true hvis seksjonen `s` vil være i scope (dvs. synlig som arvet
 * ancestor) i target-konteksten. Brukes til å re-pare override-kjeder når
 * seksjoner kopieres til en annen kontekst.
 */
export function isInScopeAncestor(
    s: Seksjon,
    targetKind: 'delomrade' | 'gruppe' | 'delomradetype'
): boolean {
    if (s.kommunal || s.seksjonstype === 'Kommune') return true;
    if (s.seksjonstype === 'Plan') return true;
    if (targetKind !== 'delomradetype' && s.seksjonstype === 'Delområdetype') return true;
    if (targetKind === 'delomrade' && s.seksjonstype === 'Delområdegruppe') return true;
    return false;
}

/**
 * Vandrer opp i override-kjeden fra `sourceParentGlobalId` og returnerer
 * globalId til første ancestor som faktisk vil være i scope i target-konteksten.
 * Returnerer undefined hvis ingen slik ancestor finnes (fallback til plain direct).
 */
export function resolveTargetParentGlobalId(
    sourceParentGlobalId: string | undefined,
    all: readonly Seksjon[],
    targetKind: 'delomrade' | 'gruppe' | 'delomradetype'
): string | undefined {
    if (!sourceParentGlobalId) return undefined;
    const byGlobalId = new Map(all.map((s) => [s.globalId, s] as const));
    const visited = new Set<string>();
    let currentId: string | undefined = sourceParentGlobalId;
    while (currentId && !visited.has(currentId)) {
        visited.add(currentId);
        const current = byGlobalId.get(currentId);
        if (!current) return undefined;
        if (isInScopeAncestor(current, targetKind)) return current.globalId;
        currentId = current.parentGlobalId;
    }
    return undefined;
}
